<template>
  <div class="payment-modal">
    <!-- Client Header with Balance Info -->
    <div class="client-header">
      <div class="client-avatar">
        <span class="avatar-text">{{ getInitials(currentClient?.fullname_client) }}</span>
      </div>
      <div class="client-info">
        <h3 class="client-name">{{ currentClient?.fullname_client || 'Select Client' }}</h3>
        <div class="balance-badge" :class="{ 'overdue': currentClient?.balance_client > 0 }">
          <span class="badge-label">Current Balance:</span>
          <span class="badge-amount">₱{{ formatNumber(currentClient?.balance_client) }}</span>
        </div>
      </div>
    </div>

    <!-- Payment Form -->
    <form ref="formRef" @submit.prevent="handleSubmit" class="payment-form">
      <div class="form-section">
        <h4 class="section-title">Payment Details</h4>
        
        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-money-bill-wave"></i>
            Amount to Pay
          </label>
          <div class="input-group">
            <span class="input-group-text">₱</span>
            <input 
              type="number" 
              ref="amountRef"
              v-model="transaction.amount_te" 
              class="form-control amount-input text-end"
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
          <small class="form-hint" v-if="currentClient?.monthlydue_client">
            Minimum due: ₱{{ formatNumber(currentClient.monthlydue_client) }}
          </small>
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-calendar-alt"></i>
            Date Issued
          </label>
          <input 
            type="date" 
            v-model="transaction.dateissued_te" 
            class="form-control"
          />
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-sticky-note"></i>
            Notes/Remarks
          </label>
          <textarea 
            v-model="transaction.transref_te" 
            class="form-control notes-input"
            rows="3"
            placeholder="Add any additional notes or remarks..."
          ></textarea>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button 
          class="btn btn-success btn-save" 
          type="button" 
          @click="handleSaveOnly"
        >
          <i class="bi bi-save"></i>
          <span>SAVE ONLY</span>
        </button>
        
        <button 
          class="btn btn-secondary btn-print" 
          type="button" 
          @click="printReceipt"
        >
          <i class="bi bi-printer"></i>
          <span>SAVE &amp; PRINT</span>
        </button>
        
        <button 
          class="btn btn-danger btn-cancel" 
          type="button" 
          @click="emit('cancel')"
        >
          <i class="bi bi-x-circle-fill"></i>
          <span>CANCEL</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import confirmationService from '@/services/confirmationService';
import paymentService from '@/services/paymentService';
import toastService from '@/services/toastService';
import receiptPrinter from '@/utils/receiptPrinter';
import { nextTick, ref } from 'vue';
/* eslint-disable */
const emit = defineEmits(['cancel', 'payment-accepted'])
const formRef = ref(null);
const printAfter = ref(false);
const amountRef = ref(null);
const transaction = ref({
    sysfk_client_te: 0, 
    amount_te: 0, 
    module_te: 'PAYMENT',
    dateissued_te: new Date().toISOString().split('T')[0],
    transref_te: ''
});


const currentClient = ref({});

const setClient = (client) => {
    currentClient.value = { ...client };
    transaction.value.sysfk_client_te = currentClient.value.syspk_client;
    transaction.value.amount_te = currentClient.value.balance_client >= client.monthlydue_client ? 
        client.balance_client : client.monthlydue_client;
    transaction.value.transref_te = '';
    transaction.value.username_client = client.username_client;
    transaction.value.fullname_client = client.fullname_client;
    transaction.due = client.due_client;
    nextTick(()=>{
        amountRef.value.focus();
        amountRef.value.select();
    });
}

const handleSubmit = async () => {
    const confirmed = await confirmationService.show(`Are you sure you want to proceed?`);
    if(!confirmed)return;
    const resp = await paymentService.acceptPayment(transaction.value);
    if(resp.syspk_te > -1){
        //success
        toastService.success("Payment saved successfully!");
        emit('payment-accepted',currentClient.value);
        if(printAfter.value){
            //execute print
        }

    }
    else{
        toastService.error(resp.recMarker);
    }
}

const printReceipt = () => {
    // Use with your transaction data
    const receiptData = {
        storeName: 'EXODOUS I.T. SOLUTIONS',
        storeAddress: '123 Business Ave, City',
        storePhone: 'Tel: (123) 456-7890',
        cashier: 'CASHIER NAME',
        items: [
            { qty: 1, name: 'Product Name', price: 100.00 }
            // Add items from your transaction
        ],
        subtotal: 100.00,
        tax: 12.00,
        total: 112.00,
        payment: 112.00,
        change: 0,
        paymentMethod: 'CASH',
        receiptNumber: receiptPrinter.generateReceiptNumber()
    };
    
    // Method 1: Using iframe (recommended)
    receiptPrinter.printReceipt(receiptData, { autoPrint: true });
    
    // Method 2: Using popup window
    // receiptPrinter.printPopup(receiptData);
};

const handleSavePrint = ()=>{
    printAfter.value = true;
    formRef.value.requestSubmit();
}

const handleSaveOnly = ()=>{
    printAfter.value = false;
    formRef.value.requestSubmit();
}

const getInitials = (fullname) => {
    if (!fullname) return '?';
    return fullname.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
}

const formatNumber = (value) => {
    if (!value && value !== 0) return '0.00';
    return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

defineExpose({ setClient });
</script>

<style scoped>
.payment-modal {
  max-width: 500px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Client Header Styles */
.client-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.client-avatar {
  flex-shrink: 0;
}

.avatar-text {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  font-size: 24px;
  font-weight: 600;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.client-info {
  flex: 1;
}

.client-name {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
}

.balance-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  font-size: 14px;
}

.balance-badge.overdue {
  background: rgba(255, 99, 99, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.badge-label {
  opacity: 0.9;
}

.badge-amount {
  font-weight: 700;
  font-size: 16px;
}

/* Form Styles */
.payment-form {
  padding: 24px;
}

.form-section {
  margin-bottom: 30px;
}

.section-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.form-label i {
  color: #667eea;
  width: 18px;
}

.input-group {
  display: flex;
  align-items: stretch;
}

.input-group-text {
  display: flex;
  align-items: center;
  padding: 0 15px;
  background: #f8f9fa;
  border: 2px solid #e1e5e9;
  border-right: none;
  border-radius: 8px 0 0 8px;
  color: #495057;
  font-weight: 500;
}

.form-control {
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: white;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.amount-input {
  border-left: none;
  border-radius: 0 8px 8px 0;
  font-size: 16px;
  font-weight: 500;
}

.notes-input {
  resize: vertical;
  min-height: 100px;
}

.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #6c757d;
}

/* Action Buttons */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 30px;
}

.btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn i {
  font-size: 16px;
}

.btn-save {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-print {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(75, 85, 99, 0.3);
}

.btn-print:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(75, 85, 99, 0.4);
}

.btn-cancel {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.btn-cancel:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 640px) {
  .payment-modal {
    max-width: 100%;
    border-radius: 0;
  }
  
  .client-header {
    padding: 20px;
  }
  
  .avatar-text {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
  
  .client-name {
    font-size: 18px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>